import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router";
import styled from "styled-components";
import AppContext from "../../../AppContext";
import { APP_TITLE } from "../../../config/constant";
import * as Library from "../../../utils/Library";
import FullScreenLoader from "../../common/loaders/FullScreenLoader";
import MainWindowHeader, { CustomSearch } from "../../common/mainApp/mainWindowHeaders/MainWindowHeader";
import { IntegrationCard } from "./POSPagesComponents/IntegrationCard";
import { RequirementsCard } from "./POSPagesComponents/RequirementsCard";
import { POSSetupSettingsMenuComponent } from "./StoresPage";
import FullScreenOverlay from "../../common/overlay/FullScreenOverlay";
import DialogBoxComponent from "../../common/dialogs/DialogBoxComponent";
import PopupContext from "../../../PopupContext";

// #region SCOPED CSS
const StyledPage = styled.div``;
// #endregion

// #region GLOBALS
export const POS_INTEGRATED = { FRONT_SYSTEMS: "689aea8f435f2ce56b14e313" }; //The list of POS systems that are currently integrated with our system
// #endregion

export default function POSConfig() {
    document.title = `${APP_TITLE} - POS Setup`;

    // #region VARIABLES
    const { MainLayoutProps, layoutType } = useOutletContext();
    const { setHasUnsavedChanges } = useContext(AppContext);
    const { setGenericConfirmationPopup } = useContext(PopupContext);
    const navigate = useNavigate();
    const integrationCardRef = useRef(null);
    const urlParams = new URLSearchParams(window.location.search);
    const connectStoreID = urlParams.get("connectStoreID");

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };
    // #endregion

    // #region UI States
    const [saveLoading, setSaveLoading] = useState(false);
    const [disconnectLoading, setDisconnectLoading] = useState(false);
    const [isLoadingBrands, setIsLoadingBrands] = useState(false);

    //----- Header Filters -----
    const [allRetailBrandsList, setAllRetailBrandsList] = useState([]);
    const [allPOSList, setAllPOSList] = useState([]); // POS dropdown data
    const [selectedRetailBrand, setSelectedRetailBrand] = useState(null); // Selected Retail Brand
    const [selectedPOS, setSelectedPOS] = useState(undefined); // {value, name}
    const [requestsEnabled, setRequestsEnabled] = useState(false); // Requests only after filters are loaded

    //----- POS Data and Connection Status-----
    const [selectedPOSConfig, setSelectedPOSConfig] = useState(null); // {_id, name, numOfSteps, connectFields: [{name, value}]}
    const [posStatus, setPosStatus] = useState(null); // POS connection status: connected, disconnected, connecting, failed

    //----- Requirements and Integration Cards -----
    const [requirementsActive, setRequirementsActive] = useState(false);
    const [integrationActive, setIntegrationActive] = useState(false);
    const [requirementsVisible, setRequirementsVisible] = useState(false);
    const [integrationVisible, setIntegrationVisible] = useState(false);
    const [requirementsCompleted, setRequirementsCompleted] = useState(false);

    //----- Save Button State (from IntegrationCard) -----
    const [integrationSaveState, setIntegrationSaveState] = useState({ enabled: false, onClick: undefined });
    // #endregion

    // #region REQUESTS
    const getRetailBrandRequest = async () => {
        if (!selectedRetailBrand || !allPOSList) return;

        const res = await Library.makeApiRequest({
            endpoint: "getRetailBrand",
            data: { retailBrandID: selectedRetailBrand?.value },
            setLoader: setIsLoadingBrands
        });
        setHasUnsavedChanges(false);

        if (res?.data?.status !== 200) return;
        const data = res?.data?.data;

        try {
            const posData = data?.POS;
            const posID = posData?.POSdata?._id;

            if (!posData || !posID || !posData?.connectFieldsV2) {
                setPosStatus("disconnected");
                setSelectedPOSConfig(null);
                setSelectedPOS(undefined);
                setRequirementsVisible(false);
                setIntegrationVisible(false);
                setRequirementsCompleted(false);
                setHasUnsavedChanges(false);

                // Auto-select POS if only one is available (after request completes to avoid race conditions)
                if (allPOSList?.length === 1) onSelectedPOSChanged(allPOSList[0]);

                if (connectStoreID) showPOSNotConfiguredPopup();
                return;
            }

            const connectStatus = posData?.status?.toLowerCase();
            setPosStatus(connectStatus);

            const selectedPos = allPOSList.find(item => item._id === posID);
            if (!selectedPos) throw new Error("The previously selected POS system is no longer available.");

            // Filter connect fields to only include the ones defined in the POS system (exclude system-injected fields)
            const allowedFields = selectedPos?.connectFields?.map(f => f.name) || [];

            const connectFieldsArray = Object.keys(posData.connectFieldsV2)
                .filter(key => allowedFields.includes(key))
                .map(key => ({
                    name: key,
                    value: posData.connectFieldsV2[key]
                }));

            const updatedPOS = {
                _id: posID,
                name: selectedPos.name,
                numOfSteps: connectFieldsArray?.length || 0,
                connectFields: connectFieldsArray
            };

            setSelectedPOSConfig(updatedPOS);
            setSelectedPOS({ name: selectedPos.name, value: posID });

            // Determine visibility based on whether this is an integrated POS
            const isIntegrated = Object.values(POS_INTEGRATED).includes(posID);

            if (connectStatus === "connected") {
                // POS already connected — show integration card in view-only mode
                showPOSIntegrationData();
            } else if (isIntegrated) {
                // Integrated POS but not yet connected — start requirements flow
                setRequirementsActive(true);
                setRequirementsVisible(true);
                setIntegrationActive(false);
                setIntegrationVisible(false);
                setRequirementsCompleted(false);
            } else {
                // Non-integrated POS, not connected — go straight to integration
                setRequirementsVisible(false);
                setIntegrationActive(true);
                setIntegrationVisible(true);
                setRequirementsCompleted(false);
            }
        } catch (error) {
            Library.showToastMessage({
                type: "error",
                message: error.message
            });
        }
    };

    const disconnectPOSRequest = async () => {
        setHasUnsavedChanges(true);

        const postData = {
            retailBrandID: selectedRetailBrand?.value
        };

        const res = await Library.makeApiRequest({
            endpoint: "X/Setup/undoSetupRetailBrandPOS",
            data: postData,
            setLoader: setDisconnectLoading
        });

        if (res?.data?.status !== 200) return;
        setHasUnsavedChanges(false);

        //refresh selected brand POS data
        await getRetailBrandRequest();

        showPOSDisconnectedPopup();
    };
    // #endregion

    // #region METHODS FOR CLICK AND EVENTS
    const onRetailBrandChange = selection => {
        setSelectedRetailBrand(selection);
        const currentRetailBrandID = selection?.value;
        sessionStorage.setItem("currentRetailBrand", currentRetailBrandID || "");

        // Reset POS-related states while new brand data loads
        setSelectedPOS(undefined);
        setSelectedPOSConfig(null);
        setPosStatus(null);
        setRequirementsVisible(false);
        setIntegrationVisible(false);
        setRequirementsCompleted(false);
    };

    const onFiltersLoaded = data => {
        const defaultRetailBrandValue =
            sessionStorage.getItem("currentRetailBrand") || localStorage.getItem("defaultRetailBrand");

        if (data?.retailBrands?.length) {
            const retailBrands = data?.retailBrands?.map(item => {
                const defaultRetailBrandID = localStorage.getItem("defaultRetailBrand");
                let retailBrandName = item?.name;

                if (item?._id === defaultRetailBrandID) {
                    retailBrandName = `${item?.name} (default)`;
                }

                return {
                    name: retailBrandName,
                    value: item?._id,
                    selected: item?._id === defaultRetailBrandValue
                };
            });

            const defaultRetailBrandObj =
                retailBrands?.find(item => item?.value === defaultRetailBrandValue) || retailBrands?.[0];

            setAllRetailBrandsList(retailBrands);
            setSelectedRetailBrand(defaultRetailBrandObj);
        }

        if (data?.posSystems?.length) {
            const formFields = data?.posSystems?.map(item => ({ ...item, name: item.name, value: item._id }));

            if (formFields.length === 1) formFields[0].selected = true;

            setAllPOSList(formFields);
        }

        setRequestsEnabled(true);
    };

    const onRefreshClicked = async () => {
        await getRetailBrandRequest();
    };

    const onSelectedPOSChanged = item => {
        if (!item) return;

        const { name, value } = item;
        const connectFields = item?.connectFields?.map(field => ({ name: field.name, value: "" })) || [];

        const selected = {
            _id: value,
            name: name,
            numOfSteps: connectFields?.length || 0,
            connectFields: connectFields
        };

        setSelectedPOSConfig(selected);
        setSelectedPOS(item);

        const isIntegrated = Object.values(POS_INTEGRATED).includes(value);

        setRequirementsActive(isIntegrated);
        setRequirementsVisible(isIntegrated);
        setIntegrationActive(!isIntegrated);
        setIntegrationVisible(!isIntegrated);
        setRequirementsCompleted(false);
    };

    const onConnectClicked = async stepsArray => {
        setHasUnsavedChanges(true);

        const connectFields = {};
        stepsArray.forEach(step => {
            connectFields[step.name] = step.value;
        });

        const postData = {
            retailBrandID: selectedRetailBrand?.value,
            posID: selectedPOSConfig?._id,
            connectFields
        };

        const res = await Library.makeApiRequest({
            endpoint: "X/Setup/setupRetailBrandPOS",
            data: postData,
            setLoader: setSaveLoading
        });

        if (res?.data?.status !== 200) return;
        setHasUnsavedChanges(false);

        //refresh selected brand POS data
        await getRetailBrandRequest();

        showPOSSavedAndConnectedPopup();
    };

    const onEditClicked = () => {
        Library.showToastMessage({
            type: "error",
            message: "Edit functionality not available yet."
        });
    };

    const onRequirementsClickContinue = () => {
        setRequirementsActive(false);
        setIntegrationActive(true);
        setIntegrationVisible(true);

        // Scroll down to the IntegrationCard after it renders
        setTimeout(() => {
            integrationCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
    };

    const showPOSIntegrationData = () => {
        setRequirementsActive(false);
        setRequirementsVisible(false);
        setIntegrationActive(false);
        setIntegrationVisible(true);
        setRequirementsCompleted(true);
    };

    const onSaveStateChanged = useCallback(saveState => {
        setIntegrationSaveState(saveState);
    }, []);
    // #endregion

    // #region Popups
    const showPOSNotConfiguredPopup = () => {
        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            headingText: "ATTENTION!",
            headingColor: "var(--text-icons-orange-500)",
            title: "POS Not Configured",
            messageHtml:
                "Your POS is not setup yet. In order to connect your stores to BOtag you need to take some steps first.",
            confirmText: "CONTINUE",
            cancelText: "NOT NOW",
            confirmState: "secondary",
            onConfirmClicked: () => {
                setGenericConfirmationPopup(false);
            },
            onCancelClicked: () => {
                //setGenericConfirmationPopup(false);
                showConfirmPOSNotConfiguredPopup();
            }
        });
    };

    const showConfirmPOSNotConfiguredPopup = () => {
        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            headingText: "JUST CHECKING...",
            headingColor: "var(--text-icons-blue-500)",
            title: "POS setup",
            messageHtml:
                "If you don’t setup your POS, BOtag cannot connect to your stores and no data will be exchanged. Please confirm you want to cancel and handle setup later.",
            confirmText: "GO TO SETUP",
            cancelText: "SETUP LATER",
            confirmState: "secondary",
            onConfirmClicked: () => {
                setGenericConfirmationPopup(false);
            },
            onCancelClicked: () => {
                setGenericConfirmationPopup(false);
                navigate("/stores");
            }
        });
    };

    const showPOSSavedAndConnectedPopup = () => {
        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            headingText: "SUCCESS!",
            headingColor: "var(--text-icons-green-500)",
            title: "Saved and connected",
            messageHtml: `POS configured correctly and connected to BOtag.<br/>
                          Last step: connect each individual store.<br/><br/>
                          Each store operates as its own fulfillment center for BOtag. Once your POS is correctly configured and connected, the store can process orders from heyBO’s growing consumers base and seamlessly exchange data with the brands you carry.`,
            confirmText: "CONNECT STORES",
            cancelText: "CLOSE",
            onConfirmClicked: () => {
                setGenericConfirmationPopup(false);
                navigate("/stores/pos-store");
            },
            onCancelClicked: () => {
                setGenericConfirmationPopup(false);
            },
            confirmState: "secondary"
        });
    };

    //TODO: FINISH THIS POPUP UI TO MATCH FIGMA
    const showDisconnectPOSPopup = () => {
        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            headingText: "ALERT!!",
            headingColor: "var(--text-icons-red-500)",
            title: "Disconnecting your POS from BOtag?",
            messageHtml:
                "Disconnecting your POS from BOtag will remove all stores from the integration. Data exchange between your stores and the brands you sell will stop, and your stores will no longer appear in the heyBO app as fulfillment centers.",
            confirmText: "DISCONNECT",
            confirmTextColor: "var(--text-icons-red-500)",
            cancelText: "CANCEL",
            onConfirmClicked: () => {
                setGenericConfirmationPopup(false);
                disconnectPOSRequest();
            },
            onCancelClicked: () => {
                setGenericConfirmationPopup(false);
            },
            confirmState: "secondary"
        });
    };

    //TODO: FINISH THIS POPUP UI TO MATCH FIGMA
    const showPOSDisconnectedPopup = () => {
        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            headingText: "SUCCESS!",
            headingColor: "var(--text-icons-green-500)",
            title: "POS disconnected!",
            messageHtml:
                "Your POS is no longer connected to BOtag.<br/>You can reconnect with the same API Key and Virtual store.",
            cancelText: "CLOSE",
            showConfirm: false,
            /*confirmText: "CONNECT",
            onConfirmClicked: () => {
                setGenericConfirmationPopup(false);
            },
            confirmState: "secondary",
            confirmTextColor: "var(--text-icons-green-500)"
            */
            onCancelClicked: () => {
                setGenericConfirmationPopup(false);
            }
        });
    };

    // #endregion

    // #region EFFECTS
    useEffect(() => {
        setupHeaders({
            props,
            allRetailBrandsList,
            onRetailBrandChange,
            onFiltersLoaded,
            selectedRetailBrand,
            allPOSList,
            onSelectedPOSChanged,
            selectedPOS,
            onRefreshClicked,
            isPOSDropdownDisabled: posStatus === "connected",
            isLayoutButtonEnabled: integrationSaveState?.enabled,
            layoutButtonOnClick: integrationSaveState?.onClick
        });
    }, [allRetailBrandsList, selectedRetailBrand, allPOSList, selectedPOS, posStatus, integrationSaveState]); // Setup headers

    useEffect(() => {
        if (!requestsEnabled) return;

        getRetailBrandRequest();
    }, [requestsEnabled, selectedRetailBrand]); // On requestsEnabled or selectedRetailBrand change => fetch POS data
    // #endregion

    // #region RENDER
    return (
        <StyledPage>
            <FullScreenLoader isLoading={isLoadingBrands} />

            <FullScreenOverlay show={saveLoading || disconnectLoading}>
                <DialogBoxComponent
                    headingColor="var(--text-icons-green-500)"
                    heading={saveLoading ? "SAVING..." : "DISCONNECTING..."}
                    showCancel={false}
                    showConfirm={false}
                    loading={saveLoading || disconnectLoading}
                />
            </FullScreenOverlay>

            {requirementsVisible && (
                <RequirementsCard
                    requirementsActive={requirementsActive}
                    selectedPOS={selectedPOS}
                    requirementsCompleted={requirementsCompleted}
                    setRequirementsCompleted={setRequirementsCompleted}
                    onRequirementsClickContinue={onRequirementsClickContinue}
                />
            )}

            {integrationVisible && (
                <div ref={integrationCardRef}>
                    <IntegrationCard
                        integrationActive={integrationActive}
                        isPOSConnected={posStatus === "connected"}
                        isConnectingAny={saveLoading || disconnectLoading}
                        isLoadingAny={isLoadingBrands || saveLoading || disconnectLoading}
                        onConnectClicked={onConnectClicked}
                        onDisconnectClicked={() => {
                            showDisconnectPOSPopup();
                        }}
                        onEditClicked={onEditClicked}
                        integrationFields={selectedPOSConfig?.connectFields}
                        onSaveStateChanged={onSaveStateChanged}
                    />
                </div>
            )}
        </StyledPage>
    );
    // #endregion
}

// #region SMALL COMPONENTS
function setupHeaders({
    props,
    allRetailBrandsList,
    onRetailBrandChange,
    onFiltersLoaded,
    selectedRetailBrand,
    allPOSList,
    onSelectedPOSChanged,
    selectedPOS,
    onRefreshClicked,
    isPOSDropdownDisabled = false,
    isLayoutButtonEnabled = false,
    layoutButtonOnClick = undefined
}) {
    props.setControlBarLeftBtns([
        { customSearch: <CustomSearch enabled={false} placeholder="Store" /* onTextChange={onSearch} */ /> }
    ]);
    props.setControlBarCenterBtns([
        { to: "/stores", active: false, disabled: false, title: "Stores" },
        { to: "/stores/staff", active: false, disabled: true, title: "Staff" },
        { to: "#", active: true, disabled: false, title: "POS" }
    ]);
    props.setControlBarRightBtns([
        { to: "#", active: true, disabled: false, title: "Config", icon: "passcode-lock" },
        { to: "/stores/pos-store", active: false, disabled: false, title: "Store", icon: "monitor" }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <MainWindowHeader
                key={window.location.pathname}
                visible={true}
                layoutTypeOptions={{
                    gridEnabled: false,
                    listEnabled: false,
                    useLayoutButton: {
                        useInsteadOfGridList: true,
                        text: "SAVE",
                        enabled: isLayoutButtonEnabled,
                        onClick: layoutButtonOnClick
                    }
                }}
                onRefreshClick={onRefreshClicked}
                firstOrganize={
                    Library.isAdminDebug()
                        ? {
                              visible: true,
                              dropdownData: allRetailBrandsList,
                              onDropdownChange: onRetailBrandChange,
                              disabled: false,
                              type: "singleSelect",
                              selectedValue: selectedRetailBrand?.name
                          }
                        : {
                              visible: true,
                              disabled: true
                          }
                }
                secondOrganize={{
                    visible: true,
                    dropdownData: allPOSList,
                    onDropdownChange: onSelectedPOSChanged,
                    disabled: isPOSDropdownDisabled,
                    type: "singleSelect",
                    selectedValue: selectedPOS?.name,
                    usePlaceholderText: "Select POS"
                }}
                pageFilters={{
                    useFilters: true,
                    filters: {
                        useRetailBrands: true,
                        usePOSSystems: true
                    },
                    onFiltersLoaded: onFiltersLoaded
                }}
                search={{
                    visible: false
                }}
                settingsButton={{
                    visible: true,
                    enabled: false,
                    active: false,
                    outlined: false,
                    layoutComponent: <POSSetupSettingsMenuComponent />
                }}
            />
        )
    });
}
// #endregion
